import { isPersonResultType, type SearchResult } from '@airclou/ui';
import { getServiceUrl, normalizeAvatarUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';

const API_BASE_URL = getServiceUrl('dashboard');

function getAuthToken(): string | null {
  return localStorage.getItem('dashboard:authToken');
}

export interface SearchFilters {
  type: string;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  hasMore: boolean;
}

/**
 * Resolves a result's image against the service that actually serves it.
 *
 * Auth owns people's photos; dashboard owns organization assets — workspace and
 * company logos, stored under its own `/files` tree (the prefixing
 * `WorkspaceSettingsTab` does). `normalizeAvatarUrl` assumes the auth case and
 * sends any relative path there, which 404s a logo. That was invisible until the
 * result row started rendering images, at which point it would have been a broken
 * tile on every workspace with a logo.
 *
 * Keyed on people rather than on logos deliberately: `tenants.logo_url` already
 * exists, so the day the search route starts selecting it for `company` rows, a
 * `type === 'workspace'` test here would have sent it to auth all over again.
 * People's photos also keep going through `normalizeAvatarUrl` even when
 * absolute, because it rewrites a prod auth host to local auth in dev.
 *
 * `isPersonResultType` is the shared list the row also renders by, so the frame
 * (round vs square) and the host can't drift apart.
 */
function resolveImageUrl(item: { type?: string; avatar?: string }): string | undefined {
  if (!item.avatar) return undefined;
  if (isPersonResultType(item.type)) {
    return normalizeAvatarUrl(item.avatar);
  }
  return item.avatar.startsWith('http') ? item.avatar : `${API_BASE_URL}${item.avatar}`;
}

class SearchService {
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  async search(
    query: string,
    filters: SearchFilters = { type: 'all' },
    pagination: { page?: number; limit?: number } = {}
  ): Promise<{ requestId: string; response: SearchResponse }> {
    const requestId = this.generateRequestId();
    const { page = 1, limit = 20 } = pagination;

    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (filters.type && filters.type !== 'all') params.set('type', filters.type);
      params.set('page', page.toString());
      params.set('limit', limit.toString());

      const response = await fetch(`${API_BASE_URL}/api/search?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });

      if (!response.ok) {
        throw new Error(`Search failed with status ${response.status}`);
      }

      const json = await response.json();

      if (!json.success || !json.data) {
        return { requestId, response: { results: [], total: 0, hasMore: false } };
      }

      const results: SearchResult[] = json.data.results.map((item: any) => ({
        id: item.id,
        type: item.type,
        title: item.title,
        description: item.description || '',
        excerpt: item.excerpt || '',
        url: item.url,
        lastModified: item.lastModified,
        tags: item.tags || [],
        relevance: item.relevance || 0,
        avatar: resolveImageUrl(item)
      }));

      return {
        requestId,
        response: {
          results,
          total: json.data.total,
          hasMore: json.data.hasMore
        }
      };
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  }
}

export const searchService = new SearchService();
